
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { FieldError } from "./FieldError";

interface FinancialDetailsProps {
  price: string;
  setPrice: (value: string) => void;
  arv: string;
  setArv: (value: string) => void;
  zillowLink: string;
  setZillowLink: (value: string) => void;
  contractDate: Date | undefined;
  setContractDate: (date: Date | undefined) => void;
  errors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
}

export const FinancialDetailsSection = ({
  price,
  setPrice,
  arv,
  setArv,
  zillowLink,
  setZillowLink,
  contractDate,
  setContractDate,
  errors = {},
  clearFieldError,
}: FinancialDetailsProps) => {
  const errCls = (field: string) =>
    errors[field] ? "border-destructive focus-visible:ring-destructive" : "";

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Financial Details</h2>
          <Separator />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="price">Asking Price*</Label>
              <Input 
                id="price" 
                placeholder="$200,000" 
                required 
                aria-invalid={!!errors.price}
                className={cn("bg-muted", errCls("price"))}
                value={price}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const formatted = value ? `$${parseInt(value).toLocaleString()}` : '';
                  setPrice(formatted);
                  clearFieldError?.("price");
                }} 
              />
              <FieldError message={errors.price} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="arv">After Repair Value (ARV)</Label>
              <Input 
                id="arv" 
                placeholder="$250,000"
                aria-invalid={!!errors.arv}
                className={cn("bg-muted", errCls("arv"))}
                value={arv}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const formatted = value ? `$${parseInt(value).toLocaleString()}` : '';
                  setArv(formatted);
                  clearFieldError?.("arv");
                }} 
              />
              <FieldError message={errors.arv} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zillowLink">Zillow Link (Optional)</Label>
              <Input 
                id="zillowLink" 
                placeholder="https://zillow.com/..." 
                className="bg-muted"
                value={zillowLink}
                onChange={(e) => setZillowLink(e.target.value)} 
              />
              <p className="text-xs text-muted-foreground">
                Link is auto-generated, input is optional
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contractDate">Contract Expiration Date*</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    aria-invalid={!!errors.contractDate}
                    className={cn(
                      "w-full justify-start text-left font-normal bg-muted",
                      errCls("contractDate")
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {contractDate ? format(contractDate, "PPP") : "Select a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={contractDate}
                    onSelect={(d) => { setContractDate(d); clearFieldError?.("contractDate"); }}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              <FieldError message={errors.contractDate} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
